/**
 * Client de l'API Olympe (transport pur : aucune notion d'app ici).
 *
 * Une compétition hébergée par Olympe est une « organization », identifiée par
 * son domaine : on envoie le domaine en en-tête `X-Domain` et l'API répond avec
 * l'organisation correspondante — nom, couleur, jeu, affiches en cours, et
 * l'`id` d'où l'on dérive l'URL du logo. C'est ce qui permet de ne garder
 * qu'une URL dans la conf.
 *
 * L'endpoint accepte le CORS (il reflète l'origine appelante et autorise
 * `X-Domain`), donc l'appel part directement de la webview, sans proxy.
 */

const API_BASE = 'https://api.olympe.xyz/api'
const ASSETS_BASE = 'https://assets.olympe.xyz/assets'

/**
 * Une « communication » : l'affiche que l'organisateur publie sur son site
 * (annonce, ouverture des inscriptions, lien vers une ligue…). Champs réduits à
 * ce qu'on exploite, le reste de la charge utile est ignoré.
 */
export interface Communication {
  id: number
  /** épreuve rattachée ; `null` = l'affiche vise toute l'organisation */
  challengeID: number | null
  /** 'feed' (fil d'actu du site) ou 'slider' (carrousel de la home) */
  type: string
  /** titre ; vaut souvent le libellé par défaut 'communication' */
  name: string
  targetURL: string | null
  /** texte par locale ('fr', 'en', 'de'…) — les locales servies varient */
  text: Record<string, string>
  /** timestamp unix en **secondes** (pas en millisecondes) */
  startDate: number
  endDate: number
  /** paysage 1920×600 ou 885×711 */
  image: string | null
  /** 750×600 (5:4), le format taillé pour le mobile */
  mobileImage: string | null
}

/** Réponse de `/organizations/current`, réduite à ce qu'on exploite. */
export interface Organization {
  id: number
  name: string
  /** domaine principal (ex. 'playallforone.olympelegends.com') */
  domain: string
  /** domaine public de la compétition, s'il existe */
  secondaryDomain: string | null
  /** nom de couleur, pas un hex (ex. 'orange') */
  color: string | null
  game: { id: number; name: string }
  /**
   * Affiches, indexées par type de communication : `{ "slider": [...] }` est la
   * seule clé observée, mais une clé `feed` peut apparaître — on aplatit donc
   * toutes les listes plutôt que de lire `slider`. L'API n'y met que les
   * communications **en cours** (le plus souvent : un tableau vide).
   */
  communications: Record<string, Communication[]>
}

/** URL du logo d'une organisation (image servie par le CDN Olympe). */
export function organizationLogoUrl(id: number): string {
  return `${ASSETS_BASE}/organizations/${id}/profile`
}

/** Hôte à envoyer en `X-Domain`, extrait de l'URL du catalogue. */
export function domainFromUrl(url: string): string | undefined {
  try {
    return new URL(url).hostname
  } catch {
    return undefined
  }
}

/**
 * Erreur d'un appel à l'API. **Tout** échec de ce module en passe par là :
 * l'appelant n'a jamais à distinguer un `Error` nu d'une erreur typée, et le
 * `status` lui dit de quoi il s'agit.
 *
 * - 401 / 403 : un refus assumé par Olympe (`teams-available` le fait par
 *   challenge, `/organizations/current` le fait sur un domaine qu'il ne
 *   reconnaît pas — `playallforone.com` sans le `www`) ;
 * - `NETWORK_STATUS` (0) : aucune réponse, la requête n'est pas partie ou n'est
 *   pas revenue. C'est une panne, pas un refus.
 *
 * Une annulation (`AbortSignal`) n'est pas une erreur d'API : elle remonte
 * telle quelle, en `AbortError`.
 */
export class OlympeHttpError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'OlympeHttpError'
    this.status = status
  }
}

/** `status` d'une erreur sans réponse HTTP (panne réseau, DNS, CORS…). */
export const NETWORK_STATUS = 0

/**
 * Un GET sur l'API, domaine en `X-Domain`, réponse en JSON. Toutes les routes
 * exploitées ici suivent ce contrat : une seule fonction pour que l'échec ait
 * toujours la même forme, et que le `X-Domain` ne puisse pas être oublié.
 *
 * Le **corps vide** rend `null`, et c'est un cas normal : `/pools` et
 * `/brackets` répondent `204` (donc `ok`, la garde ci-dessous ne les attrape
 * pas) quand l'étape visée n'en a aucun. Sans ce traitement, `response.json()`
 * lèverait une `SyntaxError` **nue**, hors du contrat « tout échec de ce module
 * passe par `OlympeHttpError` ». Un corps illisible, lui, reste un échec — mais
 * un échec typé.
 */
async function get(path: string, domain: string, signal?: AbortSignal): Promise<unknown> {
  let response: Response

  try {
    response = await fetch(`${API_BASE}${path}`, { headers: { 'X-Domain': domain }, signal })
  } catch (reason) {
    // l'appelant a annulé : ce n'est pas un échec de l'API
    if (reason instanceof DOMException && reason.name === 'AbortError') throw reason
    throw new OlympeHttpError(
      NETWORK_STATUS,
      `Olympe: ${path} injoignable pour le domaine « ${domain} » (${String(reason)})`,
    )
  }

  if (!response.ok) {
    throw new OlympeHttpError(
      response.status,
      `Olympe: HTTP ${response.status} sur ${path} pour le domaine « ${domain} »`,
    )
  }

  // 204 / 205 : pas de corps, par contrat HTTP — inutile d'aller le lire
  if (response.status === 204 || response.status === 205) return null

  const body = await response.text()
  // un 200 au corps vide existe aussi (observé sur les routes de collection)
  if (body.trim() === '') return null

  try {
    return JSON.parse(body) as unknown
  } catch (reason) {
    throw new OlympeHttpError(
      response.status,
      `Olympe: réponse illisible sur ${path} pour le domaine « ${domain} » (${String(reason)})`,
    )
  }
}

export async function fetchOrganization(
  domain: string,
  signal?: AbortSignal,
): Promise<Organization> {
  const payload = await get('/organizations/current', domain, signal)

  // seul appelant de `get` qui castait sans rien vérifier : depuis que le corps
  // vide rend `null`, ce cast laisserait passer un `null` jusqu'aux lectures de
  // `useCompetition` (`organization.game`). Les autres appelants sont déjà
  // défensifs (`Array.isArray`, `toMatch`, `nested`).
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    throw new OlympeHttpError(
      NETWORK_STATUS,
      `Olympe: /organizations/current n'a rien rendu pour le domaine « ${domain} »`,
    )
  }

  return payload as Organization
}

/**
 * Le type d'une étape de challenge. Olympe en rend quatre ; toute autre valeur
 * est conservée telle quelle (l'union est ouverte) plutôt que d'être écartée —
 * une étape inconnue reste une étape.
 */
export type StepType = 'play' | 'break' | 'register' | 'summary'

/**
 * Une étape (« step ») d'un challenge : la phase de jeu, la trêve, la fenêtre
 * d'inscription, le récapitulatif.
 *
 * L'étape ne porte **pas** le classement : c'est la **poule** qui le porte
 * (`fetchPoolRanking`). L'étape ne sert plus qu'à choisir de quelles poules et
 * de quels brackets on parle (`fetchStepPools` / `fetchStepBrackets`).
 */
export interface ChallengeStep {
  id: number
  name: string
  /** 'play' | 'break' | 'register' | 'summary', ou tout autre libellé rendu par l'API */
  type: StepType | string
  /** début de la fenêtre ; `null` quand l'API n'en donne pas */
  startDate: Date | null
  endDate: Date | null
}

/**
 * Une **division** d'un challenge (Olympe dit « segment ») : « Championnat de
 * France », « Division 2 »… C'est elle qui donne un nom à `pool.segmentID` —
 * sans quoi les 25 poules d'un step sont quatre « Poule A » homonymes répétées
 * huit fois.
 *
 * Elle voyage **déjà** dans la réponse de `/challenges` : il n'y a pas de route
 * à appeler pour l'obtenir.
 */
export interface ChallengeSegment {
  id: number
  /** rang de la division ; les divisions sans rang ferment la liste */
  position: number
  name: string
}

/**
 * Une « épreuve » de la compétition (ligue, tournoi, showmatch…). Champs
 * réduits à ce qu'on exploite.
 */
export interface Challenge {
  id: number
  name: string
  /** langue de l'épreuve ('FR', 'EN'…) */
  language: string
  /** saison / regroupement, quand l'organisateur en a défini un */
  group: { id: number; name: string } | null
  /** nombre d'équipes engagées */
  teamsParticipantsCount: number
  /** étapes du challenge, dédoublonnées et triées par date de début */
  steps: ChallengeStep[]
  /** divisions, dédoublonnées et triées par `position` puis par id */
  segments: ChallengeSegment[]
}

/** Un timestamp unix en **secondes** → `Date` ; `null` si ce n'en est pas un. */
function toDateFromSeconds(value: unknown): Date | null {
  return typeof value === 'number' && Number.isFinite(value) ? new Date(value * 1000) : null
}

/** Un nombre exploitable, 0 par défaut : l'API laisse des compteurs à `null`. */
function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/**
 * Les étapes d'un challenge, dédoublonnées par id et triées par date de début
 * (celles sans date ferment la liste — il n'y a rien pour les situer).
 */
function toSteps(items: unknown): ChallengeStep[] {
  if (!Array.isArray(items)) return []

  const seen = new Set<number>()
  const steps: ChallengeStep[] = []

  for (const item of items as unknown[]) {
    if (typeof item !== 'object' || item === null) continue

    const source = item as Record<string, unknown>
    const id = source.id
    if (typeof id !== 'number' || !Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)

    steps.push({
      id,
      name: nonEmptyString(source.name) ?? '',
      type: nonEmptyString(source.type) ?? '',
      // `startDate` / `endDate` sont en **secondes**, comme partout ici
      startDate: toDateFromSeconds(source.startDate),
      endDate: toDateFromSeconds(source.endDate),
    })
  }

  return steps.sort((a, b) => {
    const timeA = a.startDate?.getTime() ?? null
    const timeB = b.startDate?.getTime() ?? null
    if (timeA === null || timeB === null) {
      if (timeA === timeB) return 0
      return timeA === null ? 1 : -1
    }
    return timeA - timeB
  })
}

/**
 * Les divisions d'un challenge, dédoublonnées par id et triées par `position`
 * puis par id.
 *
 * **Pas** de `toCount` pour `position` : son défaut de `0` mettrait une
 * division sans rang **en tête**, donc en « mieux classée » — c'est elle que le
 * podium de la page Compétition irait chercher. Le défaut est
 * `Number.MAX_SAFE_INTEGER` : sans rang, on ferme la liste.
 */
function toSegments(items: unknown): ChallengeSegment[] {
  if (!Array.isArray(items)) return []

  const seen = new Set<number>()
  const segments: ChallengeSegment[] = []

  for (const item of items as unknown[]) {
    if (typeof item !== 'object' || item === null) continue

    const source = item as Record<string, unknown>
    const id = source.id
    if (typeof id !== 'number' || !Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)

    const position = source.position

    segments.push({
      id,
      position:
        typeof position === 'number' && Number.isFinite(position)
          ? position
          : Number.MAX_SAFE_INTEGER,
      name: nonEmptyString(source.name) ?? '',
    })
  }

  return segments.sort((a, b) => a.position - b.position || a.id - b.id)
}

/**
 * Un objet → un challenge. Défensif comme `toMatch` : seul l'`id` est
 * obligatoire, tout le reste a un défaut. On ignore ce qui ne porte pas d'id
 * plutôt que de fabriquer une entrée fantôme dans le filtre.
 */
export function toChallenge(item: unknown): Challenge | null {
  if (typeof item !== 'object' || item === null) return null

  const source = item as Record<string, unknown>
  const id = source.id
  if (typeof id !== 'number' || !Number.isFinite(id)) return null

  const group = nested(source, 'group')
  const groupId = group.id
  const groupName = nonEmptyString(group.name)

  return {
    id,
    name: nonEmptyString(source.name) ?? '',
    language: nonEmptyString(source.language) ?? '',
    group:
      typeof groupId === 'number' && groupName !== null ? { id: groupId, name: groupName } : null,
    teamsParticipantsCount: toCount(source.teamsParticipantsCount),
    steps: toSteps(source.steps),
    segments: toSegments(source.segments),
  }
}

/** Options communes aux routes qui savent se restreindre à ce qui est en cours. */
export interface ActiveOptions {
  /**
   * `true` (défaut) : seulement ce qui est en cours — le comportement historique
   * de l'app. `false` : **tout**, archives comprises.
   */
  active?: boolean
}

/**
 * Les épreuves de l'organisation.
 *
 * `active: true` (défaut) ne rend que celles en cours — c'est ce que le filtre
 * de la page Compétition veut. `active: false` appelle `/challenges` sans
 * paramètre et rend **tout** l'historique (23 challenges contre 1 actif sur
 * `www.playallforone.com`) : il n'y a pas de drapeau « terminé » dans la charge
 * utile, une épreuve passée est simplement une épreuve de la liste complète
 * absente de la liste active (cf. `useChallengeArchive`).
 */
export async function fetchChallenges(
  domain: string,
  { active = true }: ActiveOptions = {},
  signal?: AbortSignal,
): Promise<Challenge[]> {
  // tableau nu, contrairement aux réponses enveloppées du reste de l'API
  const payload = await get(active ? '/challenges?active=true' : '/challenges', domain, signal)
  const list: unknown[] = Array.isArray(payload) ? payload : []

  const seen = new Set<number>()
  const challenges: Challenge[] = []

  for (const item of list) {
    const challenge = toChallenge(item)
    if (!challenge || seen.has(challenge.id)) continue
    seen.add(challenge.id)
    challenges.push(challenge)
  }

  return challenges
}

/** Une équipe telle que l'API la rend (dans `/teams` comme dans `teams-available`). */
export interface Team {
  /** uuid composite (deux uuid collés), pas un entier comme le reste de l'API */
  id: string
  name: string
  /** code ISO à 2 lettres ('FR'…) ; `null` quand l'équipe n'en déclare pas */
  nationality: string | null
  /**
   * URL publique du logo envoyée par l'API ; `null` si absente — c'est le cas
   * de **toutes** les équipes de `teams-available`, qui n'a pas de clé `image` :
   * le logo se dérive alors de l'id (`teamLogoUrl`).
   */
  image: string | null
}

/**
 * URL du logo d'une équipe sur le CDN Olympe. Filet de sécurité : c'est
 * `Team.image` qui fait foi, ce motif ne sert que lorsque l'API n'envoie rien.
 */
export function teamLogoUrl(id: string): string {
  return `${ASSETS_BASE}/teams/${id}/profile`
}

/**
 * Un objet → une équipe. L'objet *est* l'équipe (la forme rendue par
 * `teams-available` comme par les deux camps d'un match). Tout ce qui ne porte
 * pas d'`id` et de `name` exploitables est écarté plutôt que deviné — c'est le
 * cas de l'objet réduit `{ score: 0 }` d'un adversaire pas encore connu.
 */
function toTeam(item: unknown): Team | null {
  if (typeof item !== 'object' || item === null) return null

  // les noms d'équipe arrivent parfois préfixés d'espaces (tri et affichage)
  const { id, name, nationality, image } = item as Record<string, unknown>
  if (typeof id !== 'string' || typeof name !== 'string') return null

  const country = typeof nationality === 'string' ? nationality.trim() : ''
  const logo = typeof image === 'string' ? image.trim() : ''

  return {
    id,
    name: name.trim(),
    nationality: country === '' ? null : country,
    image: logo === '' ? null : logo,
  }
}

/** Réponse de `/challenges/:id/teams-available`, remodelée pour l'app. */
export interface ChallengeTeams {
  teams: Team[]
  /** date de **calcul** de la liste (cache serveur de 15 min) ; `null` si absente */
  generatedAt: Date | null
}

/**
 * Les équipes prêtes à jouer un challenge : celles dont l'effectif et les
 * tickets sont au complet, donc éligibles à une poule ou un bracket. Ce n'est
 * pas la liste des inscrits — le nombre diffère du `teamsParticipantsCount` du
 * challenge.
 *
 * Contrairement à `/challenges/:id/participants` (401 sans session, abandonné),
 * cette route répond sans authentification. Ne **jamais** la remplacer par
 * `/teams?challengeID=` : le filtre y est ignoré et l'API rend toutes les
 * équipes de l'organisation, soit exactement ce que l'écran ne doit pas montrer.
 */
export async function fetchChallengeTeams(
  challengeId: number,
  domain: string,
  signal?: AbortSignal,
): Promise<ChallengeTeams> {
  // certains challenges répondent 403 « It's not your challenge » : un refus,
  // pas une panne — l'appelant en fait un état dédié, à partir du `status` de
  // l'`OlympeHttpError`
  const payload = await get(`/challenges/${challengeId}/teams-available`, domain, signal)

  // forme documentée : `{ generationDate, teams }` ; le tableau nu est accepté
  // par précaution, comme sur les autres routes
  const record: Record<string, unknown> =
    typeof payload === 'object' && payload !== null && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {}

  const list: unknown[] = Array.isArray(payload)
    ? (payload as unknown[])
    : Array.isArray(record.teams)
      ? (record.teams as unknown[])
      : []

  // une même équipe ne devrait pas revenir deux fois : on dédoublonne par id
  const seen = new Set<string>()
  const teams: Team[] = []

  for (const item of list) {
    const team = toTeam(item)
    if (!team || seen.has(team.id)) continue
    seen.add(team.id)
    teams.push(team)
  }

  // `generationDate` est en **secondes**, comme `matchDate` et les affiches
  const generationDate = record.generationDate

  return {
    teams,
    generatedAt:
      typeof generationDate === 'number' && Number.isFinite(generationDate)
        ? new Date(generationDate * 1000)
        : null,
  }
}

/**
 * Une diffusion du match : l'API parle de « casters » (le streamer, son pseudo
 * et son lien), l'app n'en retient que l'URL — le libellé du bouton se dérive
 * de la plateforme (`streamLabel`), pas du pseudo.
 */
export interface MatchStream {
  id: string
  url: string
}

/**
 * Un match, remodelé pour la vignette de match.
 *
 * Deux pièges de la charge utile justifient ce remodelage :
 * - le score du match est porté par **chaque équipe** (`team1.score`), pas par
 *   le match ; `scores[]` est le détail *par map*, une autre information (on
 *   n'en garde que le nombre) ;
 * - quand l'adversaire n'est pas encore connu, `team1`/`team2` vaut un objet
 *   réduit `{ score: 0 }`, sans `id` ni `name` — d'où le `Team | null` ici, que
 *   l'UI rend en « À déterminer ».
 */
export interface Match {
  /** uuid composite, comme les équipes */
  id: string
  challengeId: number
  challengeName: string
  /** phase de l'épreuve ('Semi Pro 2', 'Showmatch'…) ; `null` si l'API n'en donne pas */
  segment: string | null
  round: number | null
  /**
   * Rang du match **dans sa colonne de tour** (1 = tout en haut), tel que
   * l'organisateur a rangé son arbre. Hors bracket, l'API ne l'envoie pas :
   * `null`. C'est l'ordre vertical de `model/bracket.ts`.
   */
  position: number | null
  /** bracket du tournoi à double élimination ; `null` hors bracket */
  bracket: 'winner' | 'loser' | null
  /**
   * Le match qui reçoit le **vainqueur** (`nextmatchWinner`) ; `null` = fin de
   * l'arbre (la grande finale). C'est l'arête qui relie deux cases du bracket :
   * on ne la déduit pas des positions, l'API la donne.
   */
  nextWinnerId: string | null
  /**
   * Le match qui reçoit le **perdant** (`nextmatchLoser`) ; `null` = éliminé.
   * En double élimination, il pointe du winner bracket vers le loser bracket —
   * donc d'un arbre vers l'autre.
   */
  nextLoserId: string | null
  /** `null` = date pas encore fixée (fréquent) */
  date: Date | null
  /** `null` = adversaire pas encore déterminé */
  team1: Team | null
  team2: Team | null
  /** manches gagnées, côté team1 / team2 */
  score1: number
  score2: number
  winnerId: string | null
  forfeit1: boolean
  forfeit2: boolean
  /** nombre de maps jouées (longueur de `scores`) : 0 = match pas encore joué */
  mapsPlayed: number
  streams: MatchStream[]
}

/** Les diffusions exploitables : sans URL http(s), il n'y a rien à ouvrir. */
function toStreams(items: unknown): MatchStream[] {
  if (!Array.isArray(items)) return []

  const streams: MatchStream[] = []

  for (const item of items as unknown[]) {
    if (typeof item !== 'object' || item === null) continue

    const { id, castUrl } = item as Record<string, unknown>
    if (typeof castUrl !== 'string') continue

    const url = castUrl.trim()
    if (!/^https?:\/\//i.test(url)) continue

    // l'URL fait un identifiant de repli acceptable : elle sert de clé de liste
    streams.push({ id: typeof id === 'string' && id !== '' ? id : url, url })
  }

  return streams
}

/**
 * Le round du match. L'API l'envoie dans un tableau à un élément (`[4]`, ou
 * `[null]` hors bracket) : on prend la première valeur numérique.
 */
function firstRound(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (!Array.isArray(value)) return null

  for (const entry of value as unknown[]) {
    if (typeof entry === 'number' && Number.isFinite(entry)) return entry
  }

  return null
}

/** Le `score` d'un côté du match, y compris sur l'objet réduit `{ score: 0 }`. */
function sideScore(value: unknown): number {
  if (typeof value !== 'object' || value === null) return 0
  const score = (value as Record<string, unknown>).score
  return typeof score === 'number' && Number.isFinite(score) ? score : 0
}

/** Le bracket du tournoi ; toute autre valeur (dont `null`, hors bracket) = `null`. */
function toBracket(value: unknown): 'winner' | 'loser' | null {
  if (value === 'winner') return 'winner'
  if (value === 'loser') return 'loser'
  return null
}

/** L'API envoie 0/1, pas un booléen. */
function isForfeit(value: unknown): boolean {
  return value === 1 || value === true
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** Le sous-objet d'une clé, ou un objet vide : évite un garde par champ lu. */
function nested(source: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = source[key]
  if (typeof value !== 'object' || value === null) return {}
  return value as Record<string, unknown>
}

/**
 * Un objet → un match. Seul l'`id` est obligatoire (c'est la clé de liste et de
 * dédoublonnage) : tout le reste a un défaut, l'API laissant beaucoup de champs
 * à `null` tant que l'épreuve n'est pas jouée.
 */
export function toMatch(item: unknown): Match | null {
  if (typeof item !== 'object' || item === null) return null

  const source = item as Record<string, unknown>
  const id = nonEmptyString(source.id)
  if (!id) return null

  const challenge = nested(source, 'challenge')
  const segment = nested(source, 'segment')
  const matchDate = source.matchDate

  return {
    id,
    challengeId: typeof challenge.id === 'number' ? challenge.id : 0,
    challengeName: nonEmptyString(challenge.name) ?? '',
    segment: nonEmptyString(segment.name),
    round: firstRound(source.round),
    position: toOptionalId(source.position),
    bracket: toBracket(source.type),
    nextWinnerId: nonEmptyString(nested(source, 'nextmatchWinner').id),
    nextLoserId: nonEmptyString(nested(source, 'nextmatchLoser').id),
    // `matchDate` est en **secondes** ; `null` quand la date reste à fixer
    date:
      typeof matchDate === 'number' && Number.isFinite(matchDate)
        ? new Date(matchDate * 1000)
        : null,
    // `toTeam` rend déjà `null` sur l'objet réduit `{ score: 0 }`
    team1: toTeam(source.team1),
    team2: toTeam(source.team2),
    score1: sideScore(source.team1),
    score2: sideScore(source.team2),
    winnerId: nonEmptyString(source.teamID_winner),
    forfeit1: isForfeit(source.team1forfeit),
    forfeit2: isForfeit(source.team2forfeit),
    mapsPlayed: Array.isArray(source.scores) ? source.scores.length : 0,
    streams: toStreams(source.casters),
  }
}

/**
 * Taille de page demandée à `/matchs`.
 *
 * La route est **paginée** : `limit` (défaut **20** côté serveur, plafond
 * **100**) et `page` (0-based). Vérifié en live : `limit=101` répond
 * `{"message": "You cant display as many matches"}`, `page=abc`
 * `{"message": "Not valid input parameters"}` ; aucun total n'est rendu (ni
 * corps, ni en-tête) et aucun paramètre de tri n'est reconnu — l'ordre est
 * `matchDate` **décroissant**, les dates nulles (« à définir ») en dernier.
 *
 * C'est ce plafond de 20, et non un défaut du filtre multi-id, qui faisait
 * rendre 17 matchs à `?challengesID=9,70,239` là où trois appels unitaires en
 * rendaient 34 : 20 lignes brutes dans un cas, 20 **par challenge** dans
 * l'autre. `challengesID` accepte bien plusieurs ids (vrai `IN`).
 */
const MATCHES_PAGE_SIZE = 100

/**
 * Taille d'une page de la **queue** (`scheduled=past`), celle qui se charge au
 * défilement : de quoi remplir largement un carrousel sans payer 100 matchs
 * (~430 Ko sur le challenge 122) pour le premier écran.
 */
export const MATCHES_TAIL_SIZE = 30

/**
 * Garde-fou : 20 pages = 2 000 matchs, très au-dessus du plus gros challenge
 * observé (641 sur le 122 de `www.playallforone.com`). Une boucle qui ne
 * s'arrêterait jamais coûterait plus cher qu'une liste tronquée.
 */
const MATCHES_PAGE_MAX = 20

/**
 * L'état d'un match du point de vue de l'horloge, tel que l'API le classe
 * (`scheduled=`). Ce que les sondes ont établi :
 *
 * - le filtre s'applique **avant** la découpe en pages (les pages de
 *   `scheduled=past` font exactement `limit` jusqu'à la dernière), contrairement
 *   à `active` — c'est ce qui le rend utilisable pour paginer ;
 * - sur les matchs **datés**, `past` et `future` sont exacts et complets :
 *   `past + future` = le nombre de matchs qui ont une date, sur les trois
 *   organisations sondées ;
 * - `live` ne consulte **pas** le résultat : deux matchs commencés depuis
 *   2 h 24, sans vainqueur ni map jouée, sont classés `past`. C'est une fenêtre
 *   horaire, pas un drapeau de direct — `model/match.ts` garde donc son
 *   heuristique ;
 * - une valeur inconnue est **ignorée en silence** (`scheduled=bogus` rend la
 *   liste entière), il n'y a pas de forme multi-valeur, et un paramètre répété
 *   laisse gagner le dernier.
 */
export type MatchSchedule = 'future' | 'past' | 'live' | 'none'

/** Ce qui désigne une liste de matchs : un challenge, une étape, un état. */
export interface MatchesQuery {
  challengeId: number
  /** `null` = pas de filtre d'étape (l'épreuve entière). */
  stepId: number | null
  /** `null` = pas de filtre d'horloge (tous les matchs). */
  schedule: MatchSchedule | null
}

/**
 * La partie stable de la query.
 *
 * `stepsID` allège beaucoup la charge (298 matchs sur le Stage 1 du challenge
 * 122 contre 641 pour l'épreuve entière), mais il ne prend qu'**un seul id** —
 * `stepsID=27,34` rend exactement les 48 matchs du 27, comme
 * `/challenges/:id/pools` : le premier gagne, les autres sont ignorés.
 *
 * Un appel par challenge (et non un seul `challengesID=9,70,239`, qui
 * marcherait) : c'est le grain du cache de `useMatches`, et un challenge en
 * erreur n'efface pas les matchs des autres.
 *
 * Plus d'`active=1` nulle part : il s'applique *après* la découpe en pages
 * (sans lui les pages font exactement `limit`, avec lui elles font 38, 5, 0, 0,
 * 9, 8…), si bien qu'une page vide ne dit rien de la fin de la collection ; et
 * il vide entièrement certaines épreuves (0 match sur les 641 du challenge
 * 122). C'est `scheduled` qui joue ce rôle, correctement.
 */
function matchesQuery(target: MatchesQuery): string {
  const step = target.stepId === null ? '' : `&stepsID=${target.stepId}`
  const schedule = target.schedule === null ? '' : `&scheduled=${target.schedule}`

  return `cache=1&challengesID=${target.challengeId}${step}${schedule}`
}

/** Une page brute de `/matchs`, convertie, avec de quoi savoir si c'est la dernière. */
interface MatchPageResult {
  matches: Match[]
  /** La page n'était pas pleine : il n'y a rien après. */
  last: boolean
}

/**
 * Une page de `/matchs?<query>`.
 *
 * `toMatch` peut écarter des éléments : c'est la longueur **brute** de la
 * réponse, pas celle de la liste convertie, qui dit si la page était pleine.
 */
async function readMatchPage(
  query: string,
  page: number,
  size: number,
  domain: string,
  signal?: AbortSignal,
): Promise<MatchPageResult> {
  const payload = await get(`/matchs?${query}&limit=${size}&page=${page}`, domain, signal)
  // tableau nu, comme `/challenges` ; corps vide (`null`) = plus rien à lire
  const list: unknown[] = Array.isArray(payload) ? payload : []

  const seen = new Set<string>()
  const matches: Match[] = []

  for (const item of list) {
    const match = toMatch(item)
    if (!match || seen.has(match.id)) continue
    seen.add(match.id)
    matches.push(match)
  }

  return { matches, last: list.length < size }
}

/**
 * **Toutes** les pages d'une liste de matchs — les trois seaux de tête du
 * carrousel (`live`, `future`, `none`), qui sont petits par nature.
 *
 * La condition d'arrêt (une page incomplète) est fiable parce qu'aucun filtre
 * post-découpe n'est envoyé, cf. `matchesQuery`.
 */
export async function fetchMatches(
  target: MatchesQuery,
  domain: string,
  signal?: AbortSignal,
): Promise<Match[]> {
  return fetchAllPages(matchesQuery(target), domain, signal)
}

/** La boucle de pagination, partagée par les seaux de tête et par les brackets. */
async function fetchAllPages(
  query: string,
  domain: string,
  signal?: AbortSignal,
): Promise<Match[]> {
  const seen = new Set<string>()
  const matches: Match[] = []

  for (let page = 0; page < MATCHES_PAGE_MAX; page += 1) {
    const result = await readMatchPage(query, page, MATCHES_PAGE_SIZE, domain, signal)

    for (const match of result.matches) {
      // deux pages ne devraient pas se chevaucher, mais la liste bouge côté
      // serveur entre deux requêtes : autant que chaque résultat soit propre
      if (seen.has(match.id)) continue
      seen.add(match.id)
      matches.push(match)
    }

    if (result.last) break
  }

  return matches
}

/**
 * **Une** page d'une liste de matchs — la queue (`schedule: 'past'`), qui se
 * charge au défilement du carrousel.
 *
 * C'est ce découpage qui rend le chargement progressif possible : dans
 * `scheduled=past`, l'ordre de l'API (date décroissante) est exactement celui
 * que `sortMatches` donne aux matchs joués, donc une page qui arrive s'ajoute
 * **à la fin** de la rangée sans jamais réordonner ce que l'utilisateur
 * regarde.
 */
export async function fetchMatchesPage(
  target: MatchesQuery,
  page: number,
  domain: string,
  signal?: AbortSignal,
): Promise<MatchPageResult> {
  return readMatchPage(matchesQuery(target), page, MATCHES_TAIL_SIZE, domain, signal)
}

export type { MatchPageResult }

/**
 * Les matchs d'un **bracket**, de quoi en dessiner l'arbre (`model/bracket.ts`).
 *
 * Trois choses vérifiées en live (challenge 122, brackets 116 / 118 / 120) :
 * - le paramètre est `challengeID` au **singulier**, là où `fetchMatches` écrit
 *   `challengesID` : deux orthographes sur la même route ;
 * - **pas d'`active`**, comme partout ailleurs désormais : `active=1&bracketID=…`
 *   rendait `[]` sur les trois brackets sondés ;
 * - chaque match porte alors `round`, `position`, `nextmatchWinner` et
 *   `nextmatchLoser` — l'arbre est dans la charge utile, pas à deviner.
 *
 * Paginé comme le reste de la route : les brackets sondés tiennent en une page
 * (14, 14 et 8 matchs), mais un tableau à 128 équipes en compte 127.
 */
export async function fetchBracketMatches(
  challengeId: number,
  bracketId: number,
  domain: string,
  signal?: AbortSignal,
): Promise<Match[]> {
  return fetchAllPages(
    `cache=1&challengeID=${challengeId}&bracketID=${bracketId}`,
    domain,
    signal,
  )
}

// --- Poules et brackets ------------------------------------------------------

/**
 * Une **poule** d'un challenge : le grain réel du classement.
 *
 * Un challenge se découpe en divisions (`segmentID`), chaque division en poules
 * — et c'est la poule qui a un classement (`fetchPoolRanking`), pas l'étape. Une
 * même poule peut se jouer sur **plusieurs** étapes (`stepIds`), auquel cas son
 * classement est le même quelle que soit l'étape choisie : la route n'en prend
 * pas.
 *
 * Attention : sur une étape de playoffs, les « poules » d'Olympe sont en fait
 * des **matchs** de bracket (« LB Match 4.A », « Finale ») — rien dans la charge
 * utile ne permet de les distinguer d'une vraie poule.
 */
export interface ChallengePool {
  id: number
  name: string
  challengeId: number
  /** division de rattachement ; `null` quand l'API n'en donne pas */
  segmentId: number | null
  /** étapes où la poule se joue — souvent plusieurs */
  stepIds: number[]
  /** `poolConfigs.format` : 'FT3'… ; `null` si absent */
  format: string | null
}

/**
 * Un **bracket** (arbre à élimination) d'un challenge.
 *
 * Il s'affiche en arbre (`BracketTree`), pas en classement : ses matchs se
 * lisent par `fetchBracketMatches`. `GET /brackets/:id/ranking` reste hors
 * périmètre — une autre forme (des paliers de placement `{"0":[…]}`, sans
 * stats) et un **500** très fréquent.
 */
export interface ChallengeBracket {
  id: number
  name: string
  challengeId: number
  segmentId: number | null
  stepIds: number[]
  /** `bracketConfigs.type` : 'single' | 'double' observés ; `null` si absent */
  type: string | null
}

/**
 * La liste d'une réponse de `/pools` ou `/brackets` : la clé nommée, le tableau
 * nu par tolérance (comme `fetchChallengeTeams`), et le tableau vide quand `get`
 * rend `null` — c'est le `204` à corps vide, cas **normal** d'une étape sans
 * poule ni bracket.
 */
function toGroupList(payload: unknown, key: string): unknown[] {
  if (Array.isArray(payload)) return payload as unknown[]
  if (typeof payload !== 'object' || payload === null) return []
  const list = (payload as Record<string, unknown>)[key]
  return Array.isArray(list) ? (list as unknown[]) : []
}

/** Une liste d'ids entiers, dédoublonnée ; tout le reste est écarté. */
function toIdList(value: unknown): number[] {
  if (!Array.isArray(value)) return []

  const ids: number[] = []
  for (const item of value as unknown[]) {
    if (typeof item !== 'number' || !Number.isFinite(item) || ids.includes(item)) continue
    ids.push(item)
  }
  return ids
}

/** Un id facultatif : `null` plutôt que 0, qui serait un id comme un autre. */
function toOptionalId(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** Un objet → une poule. Défensif comme `toChallenge` : seul l'`id` est requis. */
function toChallengePool(item: unknown): ChallengePool | null {
  if (typeof item !== 'object' || item === null) return null

  const source = item as Record<string, unknown>
  const id = source.id
  if (typeof id !== 'number' || !Number.isFinite(id)) return null

  return {
    id,
    name: nonEmptyString(source.name) ?? '',
    challengeId: toCount(source.challengeID),
    segmentId: toOptionalId(source.segmentID),
    stepIds: toIdList(source.stepsID),
    format: nonEmptyString(nested(source, 'poolConfigs').format),
  }
}

/**
 * Un objet → un bracket de challenge.
 *
 * ⚠️ À ne pas confondre avec `toBracket` plus bas, qui traduit le
 * `'winner' | 'loser'` d'un match : deux notions homonymes dans l'API.
 */
function toChallengeBracket(item: unknown): ChallengeBracket | null {
  if (typeof item !== 'object' || item === null) return null

  const source = item as Record<string, unknown>
  const id = source.id
  if (typeof id !== 'number' || !Number.isFinite(id)) return null

  return {
    id,
    name: nonEmptyString(source.name) ?? '',
    challengeId: toCount(source.challengeID),
    segmentId: toOptionalId(source.segmentID),
    stepIds: toIdList(source.stepsID),
    type: nonEmptyString(nested(source, 'bracketConfigs').type),
  }
}

/**
 * Les poules d'une **étape** d'un challenge.
 *
 * Le filtre serveur `?stepsID=` fonctionne et suit l'appartenance réelle (une
 * poule jouée sur quatre étapes remonte pour chacune d'elles). Mais il ne prend
 * qu'**un seul id** : `?stepsID=203,212` rend les 25 poules du 203 et oublie les
 * 5 du 212 — exactement le piège de `challengesID` sur `/matchs`. Ne jamais
 * « optimiser » en groupant les étapes.
 *
 * Une étape sans poule répond `204` à corps vide, que `get` rend en `null` :
 * c'est un tableau vide, pas un échec.
 */
export async function fetchStepPools(
  challengeId: number,
  stepId: number,
  domain: string,
  signal?: AbortSignal,
): Promise<ChallengePool[]> {
  const payload = await get(`/challenges/${challengeId}/pools?stepsID=${stepId}`, domain, signal)

  const seen = new Set<number>()
  const pools: ChallengePool[] = []

  for (const item of toGroupList(payload, 'pools')) {
    const pool = toChallengePool(item)
    if (!pool || seen.has(pool.id)) continue
    seen.add(pool.id)
    pools.push(pool)
  }

  return pools
}

/**
 * Les brackets d'une **étape** d'un challenge. Mêmes règles que
 * `fetchStepPools` : un seul id d'étape par appel, `204` = aucun bracket.
 *
 * Il n'existe pas de route de collection hors `/challenges/:id/brackets` —
 * `GET /brackets?challengeID=…` répond 404.
 */
export async function fetchStepBrackets(
  challengeId: number,
  stepId: number,
  domain: string,
  signal?: AbortSignal,
): Promise<ChallengeBracket[]> {
  const payload = await get(`/challenges/${challengeId}/brackets?stepsID=${stepId}`, domain, signal)

  const seen = new Set<number>()
  const brackets: ChallengeBracket[] = []

  for (const item of toGroupList(payload, 'brackets')) {
    const bracket = toChallengeBracket(item)
    if (!bracket || seen.has(bracket.id)) continue
    seen.add(bracket.id)
    brackets.push(bracket)
  }

  return brackets
}

// --- Classement --------------------------------------------------------------

/**
 * Les statistiques d'une équipe au classement d'une poule, renommées : la
 * charge utile parle de moyennes (`matchsAVG`) là où ce sont en fait des
 * **points** (2 par victoire + 1 par nul, vérifié sur plusieurs entrées).
 */
export interface RankingStats {
  /** matchs joués */
  played: number
  /** matchs programmés (joués + à jouer) */
  total: number
  wins: number
  draws: number
  losses: number
  forfeits: number
  /** points de match (`matchsAVG`) : 2 par victoire, 1 par nul */
  points: number
  /** différence de matchs (`matchsDiff`) */
  diff: number
  mapsWon: number
  mapsLost: number
  mapsDrawn: number
  mapsDiff: number
  /** points de map (`mapsAVG`), même barème que `points` */
  mapsPoints: number
}

/** Une ligne du classement. */
export interface RankingEntry {
  /** rang à partir de 1, dérivé de l'ordre du tableau (l'API le rend déjà trié) */
  rank: number
  team: Team
  stats: RankingStats
}

const EMPTY_STATS: RankingStats = {
  played: 0,
  total: 0,
  wins: 0,
  draws: 0,
  losses: 0,
  forfeits: 0,
  points: 0,
  diff: 0,
  mapsWon: 0,
  mapsLost: 0,
  mapsDrawn: 0,
  mapsDiff: 0,
  mapsPoints: 0,
}

function toRankingStats(value: unknown): RankingStats {
  if (typeof value !== 'object' || value === null) return { ...EMPTY_STATS }

  const stats = value as Record<string, unknown>

  return {
    played: toCount(stats.matchsPlayed),
    total: toCount(stats.matchsTotal),
    wins: toCount(stats.matchsVictories),
    draws: toCount(stats.matchsDraws),
    losses: toCount(stats.matchsDefeats),
    forfeits: toCount(stats.matchsForfeits),
    // `matchsAVG` n'est pas une moyenne mais le total de points
    points: toCount(stats.matchsAVG),
    diff: toCount(stats.matchsDiff),
    mapsWon: toCount(stats.mapsVictories),
    mapsLost: toCount(stats.mapsDefeats),
    mapsDrawn: toCount(stats.mapsDraws),
    mapsDiff: toCount(stats.mapsDiff),
    mapsPoints: toCount(stats.mapsAVG),
  }
}

/**
 * Un tableau de classement → des lignes numérotées.
 *
 * L'API rend un **tableau nu déjà trié** (index 0 = 1er) : le rang se dérive de
 * la position, il n'est pas envoyé.
 */
function toRankingEntries(payload: unknown): RankingEntry[] {
  const list: unknown[] = Array.isArray(payload) ? payload : []

  // une équipe ne devrait pas apparaître deux fois au classement ; si ça
  // arrivait, la première ligne (la mieux classée) fait foi
  const seen = new Set<string>()
  const entries: RankingEntry[] = []

  for (const item of list) {
    if (typeof item !== 'object' || item === null) continue

    const source = item as Record<string, unknown>
    const team = toTeam(source.team)
    if (!team || seen.has(team.id)) continue
    seen.add(team.id)

    entries.push({
      // le rang suit l'ordre d'arrivée, pas l'index brut : une ligne écartée ne
      // doit pas laisser un trou dans la numérotation
      rank: entries.length + 1,
      team,
      stats: toRankingStats(source.stats),
    })
  }

  return entries
}

/**
 * Le classement d'une **poule**.
 *
 * C'est le seul classement que l'app lit : celui de l'étape
 * (`/steps/:id/ranking`) fusionnait toutes les poules et toutes les divisions
 * en une liste plate de plus de cent équipes, qui ne veut rien dire.
 *
 * La réponse est un tableau nu déjà trié, de la même forme que l'ancien
 * classement d'étape. Une poule sans match joué répond `200 []` ; une poule
 * inconnue répond `404` — une erreur comme une autre, l'écran n'en fait pas un
 * cas à part (l'écran choisit ses poules dans la liste rendue par
 * `fetchStepPools`, il n'en invente pas).
 */
export async function fetchPoolRanking(
  challengeId: number,
  poolId: number,
  domain: string,
  signal?: AbortSignal,
): Promise<RankingEntry[]> {
  return toRankingEntries(
    await get(`/challenges/${challengeId}/pools/${poolId}/ranking`, domain, signal),
  )
}

// --- Détail d'un match -------------------------------------------------------

/** URL de l'avatar d'un utilisateur, quand `user.image` manque. */
export function userAvatarUrl(id: string): string {
  return `${ASSETS_BASE}/users/${id}/profile`
}

/** Le détail d'une map jouée (une ligne de `scores[]`). */
export interface MapScore {
  name: string
  /** type de map ('control', 'hybrid'…) ; `null` si l'API n'en donne pas */
  type: string | null
  score1: number
  score2: number
  /** l'API rend un **tableau** d'id (`teamID_winner`) : on en garde le premier */
  winnerId: string | null
  /** les joueurs alignés sur cette map, côté team1 / team2 (ids utilisateur) */
  team1Players: string[]
  team2Players: string[]
}

/** Un joueur de la composition d'une équipe. */
export interface LineupMember {
  userId: string
  /** pseudo **sans** le discriminant (`bandicoot#44171` → `bandicoot`) */
  username: string
  /** avatar rendu par l'API ; `null` = à dériver de l'id (`userAvatarUrl`) */
  image: string | null
  /** rôles d'équipe ('captain', 'manager', 'owner'…) ; souvent vide */
  roles: string[]
  /** rôles de jeu (`tags.gameRoles` : 'tank'…) ; souvent absents */
  gameRoles: string[]
}

/** La composition alignée par une équipe sur le match. */
export interface Lineup {
  members: LineupMember[]
}

/**
 * Un match, enrichi de ce que seule la route unitaire `/matchs/:id` rend :
 * compositions, détail des maps et rattachement (étape, poule, bracket).
 */
export interface MatchDetail extends Match {
  /** détail par map ; vide tant que rien n'est joué */
  maps: MapScore[]
  /** composition de team1 ; `null` = l'API n'en a pas (adversaire inconnu, ou pas de feuille) */
  lineup1: Lineup | null
  lineup2: Lineup | null
  /** nom de l'étape (`steps[0]`) ; `null` si l'API n'en rattache aucune */
  stepName: string | null
  poolName: string | null
  bracketName: string | null
}

/** Les pseudos Olympe portent un discriminant (`foo#1234`) qu'on n'affiche pas. */
function stripDiscriminant(username: string): string {
  return username.split('#')[0]?.trim() ?? ''
}

/** Une liste de chaînes non vides ; tout le reste est ignoré. */
function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  const list: string[] = []
  for (const item of value as unknown[]) {
    const text = nonEmptyString(item)
    if (text) list.push(text)
  }
  return list
}

/**
 * Une liste de joueurs. Même forme partout — `{ user, roles, tags.gameRoles }` —,
 * qu'elle vienne de la composition d'un match (`team1.lineup.members`) ou de
 * l'effectif d'une équipe (`members` / `membersLent` de `/teams/:id`) : une
 * seule conversion pour les trois. Dédoublonnée par id ; une entrée sans
 * utilisateur exploitable est écartée plutôt que devinée.
 */
function toLineupMembers(value: unknown): LineupMember[] {
  const items: unknown[] = Array.isArray(value) ? (value as unknown[]) : []

  const seen = new Set<string>()
  const members: LineupMember[] = []

  for (const item of items) {
    if (typeof item !== 'object' || item === null) continue

    const entry = item as Record<string, unknown>
    const user = nested(entry, 'user')
    const userId = nonEmptyString(user.id)
    if (!userId || seen.has(userId)) continue
    seen.add(userId)

    const username = nonEmptyString(user.username)
    const image = nonEmptyString(user.image)

    members.push({
      userId,
      username: username ? stripDiscriminant(username) : '',
      image,
      roles: toStringList(entry.roles),
      gameRoles: toStringList(nested(entry, 'tags').gameRoles),
    })
  }

  return members
}

/**
 * Une composition. `null` quand l'API n'en rend pas (`team1.lineup === null`,
 * fréquent tant que l'adversaire n'est pas connu) ; `members` peut être vide.
 */
function toLineup(value: unknown): Lineup | null {
  if (typeof value !== 'object' || value === null) return null

  return { members: toLineupMembers((value as Record<string, unknown>).members) }
}

/** Le détail par map. `scores` peut être vide, `team2Players` absent. */
function toMapScores(value: unknown): MapScore[] {
  if (!Array.isArray(value)) return []

  const maps: MapScore[] = []

  for (const item of value as unknown[]) {
    if (typeof item !== 'object' || item === null) continue

    const source = item as Record<string, unknown>
    const map = nested(source, 'map')
    // `teamID_winner` est ici un **tableau** d'id, pas une chaîne comme sur le match
    const winners = toStringList(source.teamID_winner)

    maps.push({
      name: nonEmptyString(map.name) ?? '',
      type: nonEmptyString(map.type),
      score1: toCount(source.score1),
      score2: toCount(source.score2),
      winnerId: winners[0] ?? nonEmptyString(source.teamID_winner),
      team1Players: toStringList(source.team1Players),
      team2Players: toStringList(source.team2Players),
    })
  }

  return maps
}

/**
 * Un match et tout son détail.
 *
 * Même forme qu'un élément de `/matchs?…` (d'où la réutilisation de `toMatch`),
 * plus `steps`, `pool`, `bracket`, le détail de `scores[]` et les compositions.
 *
 * Un id inconnu — ou un match appartenant à une **autre** organisation que le
 * `X-Domain` envoyé — répond **404** : l'écran doit tenir avec un match
 * introuvable, ce n'est pas nécessairement une panne.
 */
export async function fetchMatchDetail(
  matchId: string,
  domain: string,
  signal?: AbortSignal,
): Promise<MatchDetail> {
  const payload = await get(`/matchs/${matchId}`, domain, signal)

  const base = toMatch(payload)
  if (!base) throw new OlympeHttpError(404, `Olympe: match « ${matchId} » illisible`)

  const source = payload as Record<string, unknown>
  const steps = toSteps(source.steps)

  return {
    ...base,
    maps: toMapScores(source.scores),
    lineup1: toLineup(nested(source, 'team1').lineup),
    lineup2: toLineup(nested(source, 'team2').lineup),
    // un match n'appartient qu'à une étape en pratique : on garde la première
    stepName: steps[0]?.name ?? null,
    poolName: nonEmptyString(nested(source, 'pool').name),
    bracketName: nonEmptyString(nested(source, 'bracket').name),
  }
}

// --- Détail d'une équipe -----------------------------------------------------

/**
 * Une équipe et son effectif (`GET /teams/:id`).
 *
 * Même objet que dans `teams-available` (`id`, `name`, `nationality`), plus
 * l'`image` que cette route-là ne rend pas, et surtout les deux listes de
 * joueurs. Ce qu'on ignore volontairement du reste de la charge utile :
 * `externalLinks` (réseaux sociaux, tous `null` sur les équipes sondées),
 * `recruitment`, `followers`, `segments` et les `ticketsPurchased` de chaque
 * membre (le détail des billets d'inscription, sans usage à l'écran).
 */
export interface TeamDetail extends Team {
  /** l'effectif ; même forme qu'une composition de match, d'où `LineupMember` */
  members: LineupMember[]
  /**
   * Les joueurs **prêtés** à l'équipe (marché des transferts d'Olympe). Vide
   * sur toutes les équipes sondées (les 138 du challenge 122, plus 25 équipes
   * de trois autres organisations) : la clé existe toujours, la liste est
   * presque toujours vide — et l'écran ne montre la section que si elle ne
   * l'est pas.
   */
  membersLent: LineupMember[]
}

/**
 * Le détail d'une équipe.
 *
 * Deux choses vérifiées en live : un id inconnu répond **404**
 * (`{"message": "Team not found: …"}`), et le `X-Domain` compte — une équipe de
 * `www.playallforone.com` lue sous le domaine d'une autre organisation répond
 * ce même 404. D'où la clé de cache qui embarque la compétition, côté
 * `useTeam`, exactement comme pour le détail d'un match.
 */
export async function fetchTeam(
  teamId: string,
  domain: string,
  signal?: AbortSignal,
): Promise<TeamDetail> {
  const payload = await get(`/teams/${teamId}`, domain, signal)

  // `toTeam` écarte tout ce qui n'a pas d'`id` et de `name` : un corps vide ou
  // une forme inattendue devient une erreur typée, pas un écran à moitié peint
  const team = toTeam(payload)
  if (!team) throw new OlympeHttpError(404, `Olympe: équipe « ${teamId} » illisible`)

  const source = payload as Record<string, unknown>

  return {
    ...team,
    members: toLineupMembers(source.members),
    membersLent: toLineupMembers(source.membersLent),
  }
}
